import type { ActorKind, TimelineEvent } from '../../lib/api-types';
import styles from './Timeline.module.css';

const VERBS: Record<TimelineEvent['verb'], string> = {
  created: 'created',
  updated: 'updated',
  deleted: 'deleted',
  restored: 'restored',
  linked: 'linked',
  unlinked: 'unlinked',
  call_initiated: 'initiated a call',
};

function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export type TimelineFilter = 'all' | 'user' | 'echo';

export function TimelineFilters({
  value,
  onChange,
}: {
  value: TimelineFilter;
  onChange: (filter: TimelineFilter) => void;
}) {
  const options: Array<{ value: TimelineFilter; label: string }> = [
    { value: 'user', label: 'You' },
    { value: 'echo', label: 'Your Echo' },
    { value: 'all', label: 'Everything' },
  ];
  return (
    <div className={styles.filters} role="tablist" aria-label="Timeline filter">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          className={styles.filter}
          data-active={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Timeline({ events }: { events: readonly TimelineEvent[] }) {
  if (events.length === 0) {
    return <p className={styles.empty}>Nothing here yet.</p>;
  }
  return (
    <div className={styles.timeline}>
      {events.map((event) => (
        <TimelineRow key={event.id} event={event} />
      ))}
    </div>
  );
}

function TimelineRow({ event }: { event: TimelineEvent }) {
  const changes = Object.entries(event.changes ?? {});
  return (
    <article className={styles.event}>
      <span
        className={styles.chip}
        data-kind={event.actor.kind satisfies ActorKind}
        title={event.actor.displayName}
        aria-hidden="true"
      >
        {initials(event.actor.displayName)}
      </span>
      <div className={styles.body}>
        <p className={styles.line}>
          <span className={styles.actor}>{event.actor.displayName}</span>{' '}
          <span className={styles.verb}>{event.communication
            ? `initiated a call to ${event.communication.sourceRecordLabel} · ${event.communication.number}`
            : `${VERBS[event.verb] ?? event.verb} this record`}</span>
        </p>
        {event.communication && event.communication.contextRecordId !== event.communication.sourceRecordId && (
          <p className={styles.line}>From {event.communication.contextRecordLabel}</p>
        )}
        <time className={styles.time} dateTime={event.createdAt}>
          {relativeTime(event.createdAt)}
        </time>
        {changes.length > 0 ? (
          <div className={styles.changes}>
            {changes.slice(0, 6).map(([field, diff]) => (
              <div key={field} className={styles.change}>
                <span className={styles.changeField}>{field}</span>
                <span className={styles.from}>{formatValue(diff?.from)}</span>
                <span aria-hidden="true">→</span>
                <span>{formatValue(diff?.to)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </article>
  );
}
